/**
 * QorTrace · Top 10 Threat Radars This Week leaderboard (Iter208).
 *
 * Ranks verified customer radars by score (shares × 3 + views × 1)
 * over the last 7 days. Renders nothing until at least one radar
 * has activity.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Trophy, Sparkles, ArrowRight } from "lucide-react";


const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: API });


const MEDALS = ["🥇", "🥈", "🥉"];


function rankBadge(rank) {
  if (rank <= 3) return MEDALS[rank - 1];
  return `#${rank}`;
}


function primaryLabel(item) {
  if (item.domain) return item.domain;
  if (item.social?.handle) return `@${item.social.handle}`;
  return item.slug;
}


function sublabel(item) {
  if (item.domain) {
    return item.cert_match ? "AUDIT CERT MATCH" : "DOMAIN VERIFIED";
  }
  if (item.social?.handle) {
    return item.social.platform === "linkedin"
      ? "LINKEDIN VERIFIED"
      : "X / TWITTER VERIFIED";
  }
  return "VERIFIED";
}


export default function LeaderboardSection() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancelled = false;
    client.get("/api/public/customer-radars/leaderboard?days=7&limit=10")
      .then((r) => { if (!cancelled) setItems(r.data?.items || []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <section
      id="threat-radar-leaderboard"
      className="max-w-[1400px] mx-auto px-6 sm:px-8 py-16"
      data-testid="threat-radar-leaderboard"
    >
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="qt-label" style={{ color: "var(--qt-cyan)", letterSpacing: "0.16em" }}>
            <Trophy size={11} style={{ display: "inline", marginRight: 6 }} />
            TOP 10 THREAT RADARS · LAST 7 DAYS
          </div>
          <h2
            className="qt-h2 mt-2"
            style={{ fontSize: "1.8rem", color: "#fff", lineHeight: 1.2 }}
          >
            Most-shared PQC threat radars this week
          </h2>
          <p className="mt-2" style={{ color: "var(--qt-text-2)", maxWidth: 760 }}>
            Ranked by combined share-clicks and pageviews. Updated live.
            Want your radar here next week? <Link to="/signup"
              data-testid="leaderboard-signup-cta"
              style={{ color: "var(--qt-cyan)", textDecoration: "underline" }}>
              Publish your radar — free <Sparkles size={11} style={{ display: "inline" }} />
            </Link>
          </p>
        </div>
      </div>

      <ol
        className="mt-6 grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", listStyle: "none", padding: 0, margin: 0 }}
      >
        {items.map((it) => (
          <li key={it.slug}>
            <Link
              to={`/customer/${it.slug}/threat-radar`}
              data-testid={`leaderboard-item-${it.slug}`}
              className="qt-card qt-card-interactive flex items-center gap-3 py-3 px-4"
              style={{ textDecoration: "none" }}
              title={`${it.title} — score ${it.score} (${it.shares} shares · ${it.views} views)`}
            >
              <div
                aria-hidden="true"
                style={{
                  width: 40, height: 40, borderRadius: 8, flexShrink: 0,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  background: it.rank <= 3 ? "rgba(255,212,121,0.10)" : "rgba(0,229,255,0.06)",
                  border: `1px solid ${it.rank <= 3 ? "#FFD479" : "rgba(0,229,255,0.30)"}`,
                  color: it.rank <= 3 ? "#FFD479" : "var(--qt-cyan)",
                  fontFamily: "var(--qt-mono, monospace)",
                  fontSize: it.rank <= 3 ? 22 : 13,
                  fontWeight: 700,
                }}
              >
                {rankBadge(it.rank)}
              </div>

              {it.logo_url ? (
                <img
                  src={it.logo_url}
                  alt={`${primaryLabel(it)} logo`}
                  width={28} height={28} loading="lazy"
                  style={{
                    borderRadius: 5,
                    background: "rgba(255,255,255,0.06)",
                    flexShrink: 0,
                  }}
                  onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                />
              ) : null}

              <div className="min-w-0 flex-1">
                <div className="qt-mono text-xs truncate" style={{ color: "#fff" }}>
                  {primaryLabel(it)}
                </div>
                <div className="qt-mono text-[9px]"
                     style={{ color: "var(--qt-text-3)", letterSpacing: "0.10em" }}>
                  {sublabel(it)}
                </div>
              </div>

              <div className="text-right" style={{ flexShrink: 0 }}>
                <div className="qt-mono text-xs" style={{ color: "var(--qt-cyan)" }}>
                  {it.score}
                </div>
                <div className="qt-mono text-[9px]" style={{ color: "var(--qt-text-3)" }}>
                  SCORE
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
}
